import { useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

type HistoryState = Record<string, unknown> | null;

/**
 * `useState` whose value is also stamped onto the current browser history
 * entry under `key`. Returning to that entry via history back (or a reload)
 * restores the value; reaching the page through a fresh navigation (a new
 * entry) starts from `initial`. Entries pushed while the component stays
 * mounted (e.g. tab switches) are stamped too.
 *
 * `isValid` guards the restored value — history state is untyped input.
 */
export function useHistoryEntryState<T>(
  key: string,
  initial: T,
  isValid: (value: unknown) => value is T,
): [T, (value: T) => void] {
  const location = useLocation();
  const [value, setValue] = useState<T>(() => {
    const stored = (window.history.state as HistoryState)?.[key];
    return isValid(stored) ? stored : initial;
  });

  useLayoutEffect(() => {
    const state = window.history.state as HistoryState;
    if (state?.[key] !== value) {
      window.history.replaceState({ ...state, [key]: value }, '');
    }
  }, [key, value, location.key]);

  return [value, setValue];
}
